import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnDestroy,
  OnInit,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { formatDistanceToNowStrict } from 'date-fns';
import { enUS, de } from 'date-fns/locale';
import { interval, Subject, takeUntil } from 'rxjs';

import { LinkTypes } from '@modules/card/enums/link-types.enum';
import { TargetType } from '@modules/card/enums/target-type.enum';
import { CardLinkInterface } from '@modules/card/interfaces/card-link.interface';
import { TARGET_NEW_SPECT } from '@modules/card/interfaces/card.interface';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { EventAttendanceStatus } from '@modules/section/models/attendance.model';
import { EventStates } from '@modules/section/models/event.model';
import { Locale } from 'src/environments/language.config';
import { isContentOutdated } from 'src/helpers/dates';

import { MediaComponent } from '../../media/media.component';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { TimePeriodPipe } from '../../pipes/time-period.pipe';
import { LiveIndicatorComponent } from '../../ui/live-indicator/live-indicator.component';
import { SmallSpinnerComponent } from '../../ui/small-spinner/small-spinner.component';

@Component({
  selector: 'mb-event-card',
  templateUrl: './event-card.component.html',
  styleUrls: ['./event-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    NgTemplateOutlet,
    SmallSpinnerComponent,
    LiveIndicatorComponent,
    MediaComponent,
    LocalizedDatePipe,
    TimePeriodPipe,
    TranslatePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EventCardComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private platformId = inject(PLATFORM_ID);

  public eventStates = EventStates;
  public linkTypes = LinkTypes;
  public distance?: string;
  public showImageFallback = false;
  public isArchived = false;
  public attendancesToIconMap = {
    representative: 'mb-icon-event-representative',
    has_substitute: 'mb-icon-event-representative',
    pending: 'mb-icon-event-invited',
    open: 'mb-icon-event-invited',
    confirmed: 'mb-icon-event-accepted',
    canceled: 'mb-icon-event-declined',
    declined: 'mb-icon-event-declined',
    expired: 'mb-icon-event-declined', // TBD
  };

  // TODO: make link / click events coherent across the new cards. TBD if cards take care or if parent takes care.
  // TODO: clean up input such that we can use data from the event model (once ERMv3 is gone we can clean up the model and interfaces)

  @Input() link?: CardLinkInterface;
  @Input() darkTheme: boolean = true;
  @Input() image?: MediaImageModel;
  @Input() titleHash?: string;
  @Input() headline?: string;
  @Input() startDate?: Date;
  @Input() endDate?: Date;
  @Input() timezoneOffset?: string;
  @Input() location?: string;
  @Input() attendanceStatus?: EventAttendanceStatus;
  @Input() registrationDeadline?: Date;
  @Input() eventState?: EventStates;

  private destroy$ = new Subject<boolean>();

  ngOnInit(): void {
    this.isArchived = isContentOutdated(this.startDate);

    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.startDate || this.eventState !== EventStates.UPCOMING) {
          this.destroy$.next(true);
          return;
        }

        // FIXME: Please double check that the EventStatus automatically changes from UPCOMING to RUNNING before removing this fail-safe
        //        Avoid showing "negative distance"
        if (this.startDate < new Date()) {
          this.eventState = EventStates.RUNNING;
          this.cdr.detectChanges();
          return;
        }

        this.distance = formatDistanceToNowStrict(this.startDate, {
          addSuffix: true,
          locale: this.translateService.currentLang() === Locale.DE ? de : enUS, // TODO: integrate this into app service so we can use this across the app
        });
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public isPending() {
    return (
      this.attendanceStatus === EventAttendanceStatus.OPEN || this.attendanceStatus === EventAttendanceStatus.PENDING
    );
  }

  public linkAction(e: MouseEvent) {
    if (!isPlatformBrowser(this.platformId)) return;

    const { target, query, type, url, outlets } = this.link as CardLinkInterface;
    const programmaticRouting = type === this.linkTypes.PROGRAMMATIC;

    if (target !== TargetType.BLANK && !programmaticRouting && !query) {
      return;
    }
    e.preventDefault();
    if (programmaticRouting || query) {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: {
            q: query || '',
          },
          queryParamsHandling: 'merge',
        })
        .then(() => {
          this.router.navigate([url], outlets);
        });
      return false;
    }

    window.open.apply(null, [url, '_blank', TARGET_NEW_SPECT]);
    return false;
  }
}
